import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Tooltip } from 'antd'
import { useState } from 'react'

import {
  useInvestigationsServiceDeleteExternalInvestigationResultEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimensEndpointKey
} from 'src/api/queries'
import { ApiError, InvestigationTestResultDto } from 'src/api/requests'
import { message, SaveConfirm } from 'src/components'
import { isExternalResult } from 'src/features/persons/utils'
// Direct file import: the tests components barrel re-exports the cards that use
// these actions, so going through it would close an import cycle.
import { EditTestResultModal } from 'src/features/tests/components/EditTestResultModal'
import { useResultEditPermission } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { EditExternalResultModal } from './EditExternalResultModal'

type Props = {
  result: InvestigationTestResultDto
  /** Approval state of the test the result belongs to: an approved result was
   *  already sent to the requesting doctor, so editing it needs the dedicated
   *  permission. */
  isApproved?: boolean
  onChanged?: () => void
  size?: 'small' | 'middle'
  /** Specimen the result belongs to; lets the external edit modal reach the
   *  reception fields, which live on the specimen rather than on the result. */
  specimenId?: string
  /** The referring doctor lives on the test/demand behind the result, not on
   *  the result itself, so callers pass it through for the external editor. */
  referralDoctorUserId?: string | null
  referralDoctorName?: string | null
  /** Display name of the registered referring doctor, needed so the external
   *  editor's select can show it instead of the raw id. */
  referralDoctorUserName?: string | null
  /** The investigation type classifier id, forwarded to EditTestResultModal so
   *  its result/value options are read from the same source AddResultModal
   *  uses instead of the unreliable standalone test-type endpoint. */
  typeId?: string
}

/** Edit (and, for externally sourced results, delete) actions for a recorded
 *  result. An external result opens the sample-registration modal, any other
 *  source opens the plain result editor. Renders nothing when the result has no
 *  id (can't target the endpoints) or when the user may not edit it. */
export const ResultActions = ({
  result,
  isApproved,
  onChanged,
  size = 'small',
  specimenId,
  referralDoctorUserId,
  referralDoctorName,
  referralDoctorUserName,
  typeId
}: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()
  const [isEditOpen, setIsEditOpen] = useState(false)

  const { canEditResult } = useResultEditPermission()

  const isExternal = isExternalResult(result)

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useInvestigationsServiceGetInvestigationTestsEndpointKey]
    })
    onChanged?.()
  }

  const { mutate: deleteResult, isPending } =
    useInvestigationsServiceDeleteExternalInvestigationResultEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body?.errors),
      onSuccess: () => {
        message.success(t('persons.external_result.deleted'))
        invalidate()
      }
    })

  if (!result.id || !canEditResult(isApproved)) return null

  return (
    <Flex gap={4} align="center">
      <Tooltip title={t('common.edit')}>
        <Button
          size={size}
          icon={<EditOutlined />}
          onClick={() => setIsEditOpen(true)}
        />
      </Tooltip>

      {/* Only externally entered results can be removed; the others belong to a
          laboratory test and are corrected, never deleted. */}
      {isExternal && (
        <SaveConfirm
          title={t('persons.external_result.delete_confirm')}
          onConfirm={() => result.id && deleteResult({ id: result.id })}
          okButtonProps={{ loading: isPending, danger: true }}
        >
          <Button
            size={size}
            danger
            icon={<DeleteOutlined />}
            title={t('common.delete')}
          />
        </SaveConfirm>
      )}

      {isEditOpen &&
        (isExternal ? (
          <EditExternalResultModal
            result={result}
            specimenId={specimenId}
            referralDoctorUserId={referralDoctorUserId}
            referralDoctorName={referralDoctorName}
            referralDoctorUserName={referralDoctorUserName}
            typeId={typeId}
            onCancel={() => setIsEditOpen(false)}
            onSuccess={invalidate}
          />
        ) : (
          <EditTestResultModal
            result={result}
            isApproved={isApproved}
            typeId={typeId}
            onCancel={() => setIsEditOpen(false)}
            onSuccess={invalidate}
          />
        ))}
    </Flex>
  )
}
